// Date-picker form field. Wraps the .field/.field-popover treatment from
// the design and uses CalendarPicker inside. Replaces the native
// <input type="date"> on the passengers page so the picker UX matches the
// rest of the portal.

import { useEffect, useRef, useState } from "react";
import { CalendarPicker, dateToYmd, ymdToDate } from "./CalendarPicker";

interface Props {
  label: string;
  value: string;                       // YYYY-MM-DD; "" if not set yet
  onChange: (ymd: string) => void;
  required?: boolean;
  optional?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** "Year-first" calendar caption — great for DOB / passport-issue dates. */
  yearFirst?: boolean;
  placeholder?: string;
}

export function DateField(props: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside closes the popover.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const display = props.value ? formatDisplay(props.value) : "";
  const defaultMonth = props.value
    ? ymdToDate(props.value)
    : props.yearFirst
    ? new Date(new Date().getFullYear() - 25, 0, 1)  // sensible DOB anchor
    : undefined;

  return (
    <div className="field" ref={ref} style={{ position: "relative" }}>
      <label>
        {props.label}{" "}
        {props.required && <span className="req">●</span>}
        {props.optional && <span className="opt">· optional</span>}
      </label>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="date-field-button"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {display || <span style={{ color: "var(--ink-4)" }}>{props.placeholder || "Select date"}</span>}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, opacity: 0.65 }}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      </button>
      {open && (
        <CalendarPicker
          selected={props.value ? ymdToDate(props.value) : undefined}
          onSelect={(d) => { props.onChange(dateToYmd(d)); setOpen(false); }}
          minDate={props.minDate}
          maxDate={props.maxDate}
          defaultMonth={defaultMonth}
          captionLayout={props.yearFirst ? "dropdown" : undefined}
        />
      )}
    </div>
  );
}

function formatDisplay(ymd: string): string {
  const d = ymdToDate(ymd);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
